import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
    BrokerPortalFeatureCodes,
    KeyValuePair,
    MailTemplate,
    ProductTag,
    RenewableSystemType
} from '@brokerportal/common/enums';
import { NewApplicationAndSendSmsLinkMessage, NewApplicationLinkMessage } from '@brokerportal/common/models';
import {
    InterestFreeApplicationDetails,
    LinkPrefillData,
    SavingFrequency
} from '@brokerportal/common/models/interest-free-application-details.model';
import { FeatureFlagService, MailService } from '@brokerportal/common/services';
import { HESLP_ALLOWED_SYSTEM_TYPES, HESLP_MIN_BATTERY_CAPACITY_KWH, isHeslp } from '@brokerportal/common/utils';
import { Scheme } from '@brokerportal/modules/quote/shared/models';
import { FullStateNames } from 'app/constants';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

export interface InterestFreeLinkPrefillData {
    systemCost?: number;
    deposit?: number;
    clientName?: {
        firstName?: string;
        middleName?: string;
        lastName?: string;
    };
    clientEmail?: string;
    clientPhoneNumber?: string;
    clientResidentialAddress?: {
        fullAddress?: string;
        unitNumber?: string;
        streetNumber?: string;
        street?: string;
        suburb?: string;
        postcode?: string;
        state?: string;
    };
    totalIncomeLessThanThresholdConfirmed?: boolean;
}

export interface SendLinkModalData {
    productTag: ProductTag;
    partnerId: number;
    brokerId: string;
    brokerName: string;
    brokerUserName: string;
    renewableSubsidy?: string;
    renewableRebate?: string;
    additionalData?: Record<string, any>;
    prefillData?: InterestFreeLinkPrefillData;
    isSystemCostDisabled?: boolean;
}

@Component({
    selector: 'bp-interest-free-link-generator',
    templateUrl: './interest-free-link-generator.component.html',
    styleUrls: ['./interest-free-link-generator.component.scss']
})
export class InterestFreeLinkGeneratorComponent implements OnInit, OnDestroy {
    public applicationLinkForm: FormGroup;
    public isLoading: boolean = false;
    public addressStatesForBpAddress: Array<KeyValuePair<string, string>>;
    public sortedSavingFrequencies: { key: string; value: SavingFrequency }[];
    public readonly heslpMinimumBatteryCapacityKwh = HESLP_MIN_BATTERY_CAPACITY_KWH;
    private readonly heslpAllowedSystemTypes: RenewableSystemType[] = [...HESLP_ALLOWED_SYSTEM_TYPES];
    private enableSendingInterestFreeApplicationLinkViaSms: boolean = false;

    private readonly destroyed$ = new Subject<void>();

    public get formControls() {
        return this.applicationLinkForm?.controls;
    }
    public get clientNameForm(): FormGroup | null {
        return this.formControls?.clientName as FormGroup | null;
    }
    public get clientAddressForm(): FormGroup | null {
        return this.formControls?.clientResidentialAddress as FormGroup | null;
    }
    public get electricityBillSavingsForm(): FormGroup | null {
        return this.formControls?.electricityBillSavings as FormGroup | null;
    }

    public get isAcOnly(): boolean {
        const systemTypeValue = this.formControls?.systemType?.value;
        if (!systemTypeValue || typeof systemTypeValue !== 'string' || systemTypeValue.trim() === '') {
            return false;
        }
        const systemTypes = systemTypeValue
            .split(',')
            .map(t => t.trim())
            .filter(t => !!t);
        return systemTypes.length === 1 && systemTypes[0] === RenewableSystemType.Aircon;
    }

    public get heslpLoanWarningAmount(): number {
        if (!this.isHeslp) return 0;
        const rawValue = this.formControls?.systemCost?.value;
        if (!rawValue) return 0;
        const cost = typeof rawValue === 'string' ? parseFloat(rawValue.replace(/,/g, '')) : Number(rawValue);
        if (isNaN(cost) || cost <= 15000) return 0;
        return cost - 15000;
    }

    public get interestFreeLoanWarningAmount(): number {
        if (this.isHeslp) return 0;
        const rawValue = this.formControls?.systemCost?.value;
        if (!rawValue) return 0;
        const cost = typeof rawValue === 'string' ? parseFloat(rawValue.replace(/,/g, '')) : Number(rawValue);
        if (isNaN(cost) || cost <= 45000) return 0;
        return cost - 45000;
    }

    public get isHeslp(): boolean {
        return isHeslp(this.dialogInputData);
    }

    public get dialogTitle(): string {
        return this.isHeslp ? 'Home Energy Saver loan' : 'Generate Interest-Free Application Link';
    }

    public get allowedSystemTypes(): RenewableSystemType[] | null {
        return this.isHeslp ? this.heslpAllowedSystemTypes : null;
    }

    constructor(
        public dialogRef: MatDialogRef<InterestFreeLinkGeneratorComponent>,
        @Inject(MAT_DIALOG_DATA) public dialogInputData: SendLinkModalData,
        private mailService: MailService,
        private snackBar: MatSnackBar,
        private fb: FormBuilder,
        private featureFlagService: FeatureFlagService
    ) {}

    ngOnInit(): void {
        this.applicationLinkForm = this.createApplicationForm();
        this.setupSystemTypeChangeSubscription();
        this.setupAddressConflictResolution();
        this.populateAddressStates();
        this.prepareSortedSavingFrequencies();
        this.featureFlagService
            .isEnabled(BrokerPortalFeatureCodes.EnableSendingInterestFreeApplicationLinkViaSms)
            .subscribe(enabled => (this.enableSendingInterestFreeApplicationLinkViaSms = enabled));

        // Pre-select NSW state for HESLP if not already set by prefill data
        if (this.isHeslp && !this.dialogInputData.prefillData?.clientResidentialAddress?.state) {
            this.clientAddressForm?.get('state')?.setValue('NSW');
        }

        if (this.dialogInputData.prefillData) {
            this.applicationLinkForm.patchValue(this.dialogInputData.prefillData);
        }
    }

    ngOnDestroy(): void {
        this.destroyed$.next();
        this.destroyed$.complete();
    }

    public shouldShowSolarPanelSize(): boolean {
        const systemTypeValue = this.formControls?.systemType?.value;
        if (!systemTypeValue || typeof systemTypeValue !== 'string' || systemTypeValue.trim() === '') {
            return false;
        }
        const systemTypes = systemTypeValue.split(',').map(type => type.trim());
        return systemTypes.includes(RenewableSystemType.Solar);
    }

    public shouldShowBatterySize(): boolean {
        const systemTypeValue = this.formControls?.systemType?.value;
        if (!systemTypeValue || typeof systemTypeValue !== 'string' || systemTypeValue.trim() === '') {
            return false;
        }
        const systemTypes = systemTypeValue.split(',').map(type => type.trim());
        return systemTypes.includes(RenewableSystemType.Battery);
    }

    public onCancel(): void {
        this.dialogRef.close();
    }

    public onSubmit(): void {
        if (this.applicationLinkForm.invalid) {
            this.applicationLinkForm.markAllAsTouched();
            this.displaySnackbar('Please correct the errors in the form.', true);
            return;
        }

        this.isLoading = true;
        const rawFormValue: InterestFreeApplicationDetails = this.applicationLinkForm.getRawValue();
        const dataForLinkPrefill = this.mapFormToLinkData(rawFormValue);

        const linkParameters = this.mailService.getParamatersSendToEmail(
            this.dialogInputData.brokerUserName,
            this.dialogInputData.productTag,
            dataForLinkPrefill,
            this.dialogInputData.additionalData
        );

        const commonMessageData: NewApplicationLinkMessage = {
            body: 'Here is your pre-filled application link. Please click the link to complete your application.',
            subject: 'Application Link',
            to: rawFormValue.clientEmail,
            brokerId: this.dialogInputData.brokerId,
            brokerUserName: this.dialogInputData.brokerUserName,
            productTag: this.dialogInputData.productTag,
            parameters: linkParameters
        };

        const messageData = this.enableSendingInterestFreeApplicationLinkViaSms
            ? ({
                  ...commonMessageData,
                  phoneNumber: rawFormValue.clientPhoneNumber,
                  shouldSendSms: true
              } as NewApplicationAndSendSmsLinkMessage)
            : commonMessageData;

        const mailTemplateToSend = MailTemplate.StartNewApplication;

        this.mailService
            .send(mailTemplateToSend, messageData)
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: () => {
                    this.isLoading = false;
                    this.displaySuccessMessage(messageData.to);
                    this.dialogRef.close({ success: true, emailSentTo: messageData.to });
                },
                error: errorResponse => {
                    this.isLoading = false;
                    const errorMessage =
                        errorResponse?.error?.message ||
                        errorResponse?.message ||
                        'An unexpected error occurred. Please try again.';
                    this.displaySnackbar(`Failed to send link: ${errorMessage}`, true);
                }
            });
    }

    private setupSystemTypeChangeSubscription(): void {
        const systemTypeCtrl = this.formControls?.systemType;
        if (!systemTypeCtrl) return;

        systemTypeCtrl.valueChanges.pipe(takeUntil(this.destroyed$)).subscribe((value: string | null) => {
            const systemTypes =
                value
                    ?.split(',')
                    .map(t => t.trim())
                    .filter(Boolean) ?? [];

            const solarCtrl = this.formControls?.solarPanelSize;
            if (solarCtrl) {
                if (systemTypes.includes(RenewableSystemType.Solar)) {
                    solarCtrl.setValidators([
                        Validators.required,
                        Validators.min(0.1),
                        Validators.pattern(/^\d+(\.\d{1,2})?$/)
                    ]);
                } else {
                    solarCtrl.clearValidators();
                    solarCtrl.setValue(null, { emitEvent: false });
                }
                solarCtrl.updateValueAndValidity();
            }

            const batteryCtrl = this.formControls?.batterySize;
            if (batteryCtrl) {
                if (systemTypes.includes(RenewableSystemType.Battery)) {
                    batteryCtrl.setValidators([
                        Validators.required,
                        Validators.min(this.isHeslp ? this.heslpMinimumBatteryCapacityKwh : 0.1),
                        Validators.pattern(/^\d+(\.\d{1,2})?$/)
                    ]);
                } else {
                    batteryCtrl.clearValidators();
                    batteryCtrl.setValue(null, { emitEvent: false });
                }
                batteryCtrl.updateValueAndValidity();
            }

            const electricityBillSavingsGroup = this.electricityBillSavingsForm;
            if (electricityBillSavingsGroup) {
                const acOnly = systemTypes.length === 1 && systemTypes[0] === RenewableSystemType.Aircon;
                if (acOnly) {
                    electricityBillSavingsGroup.patchValue({ amount: 0 }, { emitEvent: false });
                    electricityBillSavingsGroup.disable({ emitEvent: false });
                } else {
                    electricityBillSavingsGroup.enable({ emitEvent: false });
                }
            }
        });
    }

    private setupAddressConflictResolution(): void {
        const addressGroup = this.clientAddressForm;
        if (!addressGroup) return;

        addressGroup.valueChanges.pipe(takeUntil(this.destroyed$)).subscribe(() => {
            const fullAddressCtrl = addressGroup.get('fullAddress');
            if (!fullAddressCtrl) return;

            const hasManualEntry = ['streetNumber', 'street', 'suburb', 'postcode', 'state'].some(
                field => !!addressGroup.get(field)?.value
            );

            // Only clear selectedAddressRequired errors when fullAddress is enabled
            // (panel is closed) and manual fields have values.
            if (!fullAddressCtrl.disabled && hasManualEntry && fullAddressCtrl.hasError('selectedAddressRequired')) {
                fullAddressCtrl.setErrors(null);
            }

            // If the user manually edited address fields, clear the stale autocomplete value
            // to prevent inconsistent address data from being submitted.
            // Only trigger on dirty controls (user edits), not on autocomplete population.
            // This must run regardless of whether fullAddress is disabled (panel open)
            // because getRawValue() includes disabled controls on submit.
            const hasManualEdits = ['streetNumber', 'street', 'suburb', 'postcode', 'state'].some(
                field => !!addressGroup.get(field)?.dirty
            );

            if (hasManualEdits && fullAddressCtrl.value) {
                fullAddressCtrl.setValue(null, { emitEvent: false });
            }
        });
    }

    private createApplicationForm(): FormGroup {
        const systemCostValidators = [Validators.required, Validators.min(1001), Validators.pattern(/^\d+$/)];

        return this.fb.group({
            systemCost: [
                { value: null, disabled: this.dialogInputData.isSystemCostDisabled ?? false },
                systemCostValidators
            ],
            clientName: this.fb.group({
                firstName: ['', [Validators.required, Validators.maxLength(100)]],
                middleName: ['', Validators.maxLength(100)],
                lastName: ['', [Validators.required, Validators.maxLength(100)]]
            }),
            clientEmail: ['', [Validators.required, Validators.email, Validators.maxLength(255)]],
            clientPhoneNumber: [
                '',
                [
                    Validators.required,
                    Validators.pattern(/^(\+?\(61\)|\(\+?61\)|\+?61|\(0[1-9]\)|0[1-9])?( ?-?[0-9]){7,9}$/),
                    Validators.maxLength(20)
                ]
            ],
            clientResidentialAddress: this.fb.group(
                {
                    fullAddress: [null],
                    unitNumber: [null],
                    streetNumber: [null, Validators.required],
                    street: [null, [Validators.required, Validators.maxLength(100)]],
                    suburb: [null, [Validators.required, Validators.maxLength(100)]],
                    postcode: [null, [Validators.required, Validators.pattern(/^\d{4}$/)]],
                    state: [null, [Validators.required, this.heslpNswOnlyValidator.bind(this)]]
                },
                { validators: this.addressRequiredValidator() }
            ),
            systemType: [
                null,
                [this.systemTypeRequiredValidator.bind(this), this.heslpAllowedSystemTypesValidator.bind(this)]
            ],
            deposit: [null],
            solarPanelSize: [null],
            batterySize: [null],
            electricityBillSavings: this.fb.group({
                amount: [null, [Validators.required, Validators.min(0), Validators.pattern(/^\d+(\.\d{1,2})?$/)]],
                frequency: [null, Validators.required],
                pricingExplanationConfirmation: [false, Validators.requiredTrue]
            }),
            ...(this.dialogInputData.renewableRebate === Scheme.WesternAustralia || this.isHeslp
                ? { totalIncomeLessThanThresholdConfirmed: [false, Validators.requiredTrue] }
                : {}),
            ...(this.isHeslp ? { isReplacingEquipment: [null, Validators.required] } : {})
        });
    }

    private systemTypeRequiredValidator(control: AbstractControl): ValidationErrors | null {
        const systemTypeValue = control.value;

        if (typeof systemTypeValue === 'string' && systemTypeValue.trim() === '') {
            return { required: true };
        }

        return Validators.required(control);
    }

    private heslpAllowedSystemTypesValidator(control: AbstractControl): ValidationErrors | null {
        if (!this.isHeslp) {
            return null;
        }

        const systemTypeValue = control.value;
        if (typeof systemTypeValue !== 'string' || systemTypeValue.trim() === '') {
            return null;
        }

        const hasUnsupportedSystemType = systemTypeValue
            .split(',')
            .map(type => type.trim())
            .filter(type => !!type)
            .some(type => !this.heslpAllowedSystemTypes.includes(type as RenewableSystemType));

        return hasUnsupportedSystemType ? { heslpUnsupportedSystemTypes: true } : null;
    }

    private heslpNswOnlyValidator(control: AbstractControl): ValidationErrors | null {
        if (!this.isHeslp) {
            return null;
        }

        const stateValue = control.value;

        // If there's a value and it's not NSW, reject it
        if (stateValue && stateValue !== 'NSW') {
            return { heslpNswOnly: true };
        }

        return null;
    }

    private addressRequiredValidator(): (group: AbstractControl) => ValidationErrors | null {
        return (group: AbstractControl): ValidationErrors | null => {
            const fullAddressControl = group.get('fullAddress');
            const streetNumberControl = group.get('streetNumber');
            const streetControl = group.get('street');
            const suburbControl = group.get('suburb');
            const postcodeControl = group.get('postcode');
            const stateControl = group.get('state');

            const isManualAddressSufficient =
                streetNumberControl?.valid &&
                streetControl?.valid &&
                suburbControl?.valid &&
                postcodeControl?.valid &&
                stateControl?.valid;

            if (fullAddressControl?.errors?.['selectedAddressRequired'] && !isManualAddressSufficient) {
                return { addressSelectionOrManualRequired: true };
            }
            if (!isManualAddressSufficient) {
                return { addressIncomplete: true };
            }
            return null;
        };
    }

    private populateAddressStates(): void {
        const allStates = (Object.keys(FullStateNames) as Array<keyof typeof FullStateNames>).map(key => ({
            key: key,
            value: FullStateNames[key]
        }));

        if (this.isHeslp) {
            this.addressStatesForBpAddress = allStates.filter(state => state.key === 'NSW');
        } else {
            this.addressStatesForBpAddress = allStates;
        }
    }

    private prepareSortedSavingFrequencies(): void {
        const desiredOrder: SavingFrequency[] = [
            SavingFrequency.Quarterly,
            SavingFrequency.Monthly,
            SavingFrequency.Annually
        ];
        this.sortedSavingFrequencies = desiredOrder.map(value => ({ key: value, value: value }));
    }

    private mapFormToLinkData(formData: InterestFreeApplicationDetails): LinkPrefillData {
        const { clientName, clientResidentialAddress, electricityBillSavings } = formData;

        const linkData: LinkPrefillData = {
            clientEmail: formData.clientEmail,
            clientPhoneNumber: formData.clientPhoneNumber,
            systemCost: formData.systemCost,
            deposit: formData.deposit,
            systemType: formData.systemType,
            estimatedElectricityBillSavings: electricityBillSavings.amount,
            electricityBillSavingsFrequency: electricityBillSavings.frequency,
            pricingExplanationConfirmation: electricityBillSavings.pricingExplanationConfirmation,

            applicantFirstName: clientName.firstName,
            applicantLastName: clientName.lastName,
            applicantMiddleName: clientName.middleName || undefined,

            residentialUnitNumber: clientResidentialAddress.unitNumber,
            residentialStreetNumber: clientResidentialAddress.streetNumber,
            residentialStreetName: clientResidentialAddress.street,
            residentialSuburb: clientResidentialAddress.suburb,
            residentialPostcode: clientResidentialAddress.postcode,
            residentialState: clientResidentialAddress.state,

            brokerEmail: this.dialogInputData.brokerUserName
        };

        if (formData.solarPanelSize) {
            linkData.solarPanelSize = formData.solarPanelSize.toString();
        }
        if (formData.batterySize) {
            linkData.batterySize = formData.batterySize;
            linkData.batterySizeIndividual = formData.batterySize.toString();
        }
        if (clientResidentialAddress.fullAddress) {
            linkData.residentialFullAddress = clientResidentialAddress.fullAddress;
        }
        if (this.dialogInputData.renewableSubsidy) {
            linkData.renewableSubsidyApplication = this.dialogInputData.renewableSubsidy;
        }
        if (this.dialogInputData.renewableRebate) {
            linkData.renewableRebateApplication = this.dialogInputData.renewableRebate;
        }
        if (formData.totalIncomeLessThanThresholdConfirmed !== undefined) {
            linkData.totalIncomeLessThanThresholdConfirmed = formData.totalIncomeLessThanThresholdConfirmed;
        }
        if (formData.isReplacingEquipment !== undefined && formData.isReplacingEquipment !== null) {
            linkData.isReplacingEquipment = formData.isReplacingEquipment;
        }
        // If the system cost exceeds the $15,000 HESLP loan cap, include the minimum deposit
        // the client must contribute so the lender can pre-fill it on the application form.
        if (this.heslpLoanWarningAmount > 0) {
            linkData.deposit = this.heslpLoanWarningAmount;
        }

        // If the system cost exceeds the $45,000 interest-free loan cap, include the deposit
        // the client must contribute (loan amount = system cost - deposit, capped at $45,000).
        if (this.interestFreeLoanWarningAmount > 0) {
            linkData.deposit = this.interestFreeLoanWarningAmount;
        }

        return linkData;
    }

    private displaySuccessMessage(emailAddress: string): void {
        this.displaySnackbar(`Application link emailed successfully to ${emailAddress}`);
    }

    private displaySnackbar(message: string, isError: boolean = false): void {
        this.snackBar.open(message, 'CLOSE', {
            duration: isError ? 7000 : 5000,
            panelClass: isError ? 'error-snackbar' : 'success-snackbar',
            horizontalPosition: 'center',
            verticalPosition: 'top'
        });
    }
}
